/**
 * ChallengeCard
 * Displays a challenge in a card format
 */

import React from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  ChallengeWithStatus,
  CHALLENGE_ICONS,
  CHALLENGE_LABELS,
  CHALLENGE_COLORS,
} from "../types";

interface ChallengeCardProps {
  challenge: ChallengeWithStatus;
  onPress?: () => void;
  showLinkStatus?: boolean;
}

// Status configuration with colors and styling
// Colors are designed to work in both light and dark mode
const STATUS_CONFIG = {
  completed_by_user: {
    text: "✓ Voltooid",
    bgColor: "#8B5CF6", // Purple badge
    textColor: "#FFFFFF",
    borderColor: "#8B5CF6", // Purple border
    icon: "checkmark-circle" as const,
    opacity: 1,
  },
  active: {
    text: "Actief",
    bgColor: "#10B981", // Green badge
    textColor: "#FFFFFF",
    borderColor: "#10B981", // Green border
    icon: "play-circle" as const,
    opacity: 1,
  },
  draft: {
    text: "Concept",
    bgColor: "#F97316", // Orange badge
    textColor: "#FFFFFF",
    borderColor: "#F97316", // Orange border
    icon: "document-text" as const,
    opacity: 0.85,
  },
  ended: {
    text: "Afgelopen",
    bgColor: "#6B7280", // Gray badge
    textColor: "#FFFFFF",
    borderColor: "#6B7280", // Gray border
    icon: "flag" as const,
    opacity: 0.6,
  },
  archived: {
    text: "Gearchiveerd",
    bgColor: "#4B5563", // Darker gray badge
    textColor: "#D1D5DB",
    borderColor: "#4B5563", // Gray border
    icon: "archive" as const,
    opacity: 0.5,
  },
};

/**
 * Card component for displaying a challenge
 */
export const ChallengeCard: React.FC<ChallengeCardProps> = ({
  challenge,
  onPress,
  showLinkStatus = false,
}) => {
  const colors = useThemeColors();

  const challengeColor = CHALLENGE_COLORS[challenge.challenge_type] || colors.primary;
  const challengeIcon = CHALLENGE_ICONS[challenge.challenge_type] || "flash";
  const challengeLabel = CHALLENGE_LABELS[challenge.challenge_type] || "Uitdaging";

  // Determine the visual status of the challenge
  const getStatusConfig = () => {
    if (challenge.is_completed) {
      return STATUS_CONFIG.completed_by_user;
    }
    if (challenge.status === "draft") {
      return STATUS_CONFIG.draft;
    }
    if (challenge.status === "archived") {
      return STATUS_CONFIG.archived;
    }
    if (challenge.status === "completed") {
      return STATUS_CONFIG.ended;
    }
    if (challenge.status === "active") {
      return STATUS_CONFIG.active;
    }
    return null;
  };

  const statusConfig = getStatusConfig();
  const cardOpacity = statusConfig?.opacity ?? 1;
  const isDimmed = cardOpacity < 1;

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      className="rounded-2xl p-4 mb-3 overflow-hidden"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 2,
        borderColor: statusConfig?.borderColor || colors.border,
        opacity: cardOpacity,
      }}
    >
      <View className="flex-row items-start">
        {/* Icon */}
        <View
          className="w-12 h-12 rounded-xl items-center justify-center mr-3"
          style={{ backgroundColor: isDimmed ? colors.surfaceSecondary : `${challengeColor}20` }}
        >
          <Ionicons 
            name={challengeIcon as any} 
            size={24} 
            color={isDimmed ? colors.textTertiary : challengeColor} 
          />
        </View>

        {/* Content */}
        <View className="flex-1">
          {/* Type badge */}
          <View className="flex-row items-center mb-1">
            <Text 
              className="text-xs font-medium" 
              style={{ color: isDimmed ? colors.textTertiary : challengeColor }}
            >
              {challengeLabel}
            </Text>
            {challenge.target_type === "individual" && (
              <View className="flex-row items-center ml-2">
                <Ionicons name="person" size={10} color={colors.textSecondary} />
                <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                  Individueel
                </Text>
              </View>
            )}
            {challenge.target_type === "team" && (
              <View className="flex-row items-center ml-2">
                <Ionicons name="people" size={10} color={colors.textSecondary} />
                <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                  Team
                </Text>
              </View>
            )}
          </View>

          {/* Title */}
          <Text 
            className="font-semibold text-base" 
            style={{ color: isDimmed ? colors.textSecondary : colors.text }}
          >
            {challenge.title}
          </Text>

          {/* Description (truncated) */}
          {challenge.description && (
            <Text
              className="text-sm mt-1"
              style={{ color: isDimmed ? colors.textTertiary : colors.textSecondary }}
              numberOfLines={2}
            >
              {challenge.description}
            </Text>
          )}

          {/* Footer row */}
          <View className="flex-row items-center mt-2 flex-wrap">
            {/* Points */}
            {challenge.has_points && challenge.points > 0 && (
              <View className="flex-row items-center mr-3">
                <Ionicons name="star" size={14} color={colors.warning} />
                <Text className="text-sm ml-1 font-medium" style={{ color: colors.warning }}>
                  {challenge.points} pts
                </Text>
                {challenge.bonus_points > 0 && (
                  <Text className="text-xs ml-1" style={{ color: colors.textTertiary }}>
                    (+{challenge.bonus_points} bonus)
                  </Text>
                )}
              </View>
            )}

            {/* Link status */}
            {showLinkStatus && (
              <View className="flex-row items-center mr-3">
                {challenge.linked_event_id ? (
                  <>
                    <Ionicons name="link" size={14} color={colors.primary} />
                    <Text className="text-xs ml-1" style={{ color: colors.primary }}>
                      Gekoppeld
                    </Text>
                  </>
                ) : (
                  <>
                    <Ionicons name="unlink" size={14} color={colors.textTertiary} />
                    <Text className="text-xs ml-1" style={{ color: colors.textTertiary }}>
                      Niet gekoppeld
                    </Text>
                  </>
                )}
              </View>
            )}

            {/* Status badge - more prominent */}
            {statusConfig && (
              <View
                className="flex-row items-center px-2.5 py-1 rounded-full"
                style={{ backgroundColor: statusConfig.bgColor }}
              >
                <Ionicons name={statusConfig.icon} size={12} color={statusConfig.textColor} />
                <Text className="text-xs ml-1 font-semibold" style={{ color: statusConfig.textColor }}>
                  {statusConfig.text}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Chevron */}
        <Ionicons 
          name="chevron-forward" 
          size={20} 
          color={isDimmed ? colors.textTertiary : colors.textSecondary} 
        />
      </View>

      {/* Visual indicator stripe at top for active and completed challenges */}
      {(challenge.is_completed || challenge.status === "active") && (
        <View
          className="absolute top-0 left-0 right-0 h-1"
          style={{ 
            backgroundColor: challenge.is_completed 
              ? STATUS_CONFIG.completed_by_user.bgColor 
              : STATUS_CONFIG.active.bgColor 
          }}
        />
      )}
    </TouchableOpacity>
  );
};

export default ChallengeCard;

